import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

/**
 * Stripe-native checkout dialog.
 *
 * The old inline "Enter your card" form has been retired — Perx now always
 * redirects buyers into Stripe Checkout for real payments. This dialog is
 * a lightweight confirmation step that lets primary members pick a
 * secondary member (redeemer) before we mint the Stripe session and
 * `window.location.href` into the hosted checkout page.
 *
 * Props: open, onOpenChange, deal (obj with id, title, vendor, price),
 *        planId? (legacy, ignored — deals are one-off payments),
 *        onSuccess() (called before redirect for any local cleanup)
 */
export default function CheckoutDialog({ open, onOpenChange, deal, onSuccess }) {
    const { user } = useAuth();
    const [subUsers, setSubUsers] = useState([]);
    const [redeemerId, setRedeemerId] = useState("self");
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setRedirecting(false);
        setRedeemerId("self");
        api.get("/member/sub-users").then((r) => setSubUsers(r.data)).catch(() => { });
    }, [open]);

    const handleClose = (o) => onOpenChange(o);

    const submit = async (e) => {
        e.preventDefault();
        setRedirecting(true);
        try {
            const payload = { deal_id: deal.id };
            if (redeemerId && redeemerId !== "self") payload.sub_user_id = redeemerId;
            const { data } = await api.post("/member/checkout", payload);
            if (!data?.checkout_url) {
                throw new Error("Payment gateway did not return a checkout URL.");
            }
            // Persist intent so /payment/success can query status even after redirect
            try {
                sessionStorage.setItem(
                    "perx_last_checkout",
                    JSON.stringify({
                        session_id: data.session_id,
                        product_name: data.product_name,
                        amount: data.amount,
                        currency: data.currency,
                    })
                );
            } catch (_e) {
                // storage disabled — no-op
            }
            onSuccess?.(data);
            window.location.href = data.checkout_url;
        } catch (err) {
            setRedirecting(false);
            toast.error(formatApiError(err));
        }
    };

    if (!deal) return null;

    const redeemerName =
        redeemerId === "self"
            ? user?.name || "you"
            : subUsers.find((s) => s.id === redeemerId)?.name || "family member";

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                data-testid="checkout-dialog"
                className="max-w-lg bg-white border-navy-100"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy-700">
                        Confirm your purchase
                    </DialogTitle>
                    <DialogDescription className="text-navy-500">
                        {deal.title} · {deal.vendor}
                    </DialogDescription>
                </DialogHeader>

                <div className="my-4 rounded-lg bg-cream border border-gold-200/60 p-4 flex items-start justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-gold-600">
                            Amount today
                        </div>
                        <div className="font-display text-3xl text-navy-700 mt-1">
                            ${deal.price.toFixed(2)}
                        </div>
                    </div>
                    <ShieldCheck className="text-gold-500" />
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label className="text-navy-700 text-xs uppercase tracking-widest">
                            Redeeming for
                        </Label>
                        <Select value={redeemerId} onValueChange={setRedeemerId}>
                            <SelectTrigger
                                data-testid="checkout-redeemer"
                                className="mt-2 bg-white border-navy-100 h-11"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="self">
                                    {user?.name ? `${user.name} (you)` : "You"}
                                </SelectItem>
                                {subUsers.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.name} · {s.relationship}
                                        {s.is_dependant ? " · dependant" : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="mt-2 text-xs text-navy-500">
                            Savings from this purchase will be credited to{" "}
                            {redeemerName}.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-navy-500">
                        <Lock size={13} /> You&apos;ll complete payment securely on
                        Stripe. Perx never sees your card details.
                    </div>

                    <DialogFooter className="mt-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => handleClose(false)}
                            className="text-navy-500"
                            data-testid="checkout-cancel"
                            disabled={redirecting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={redirecting}
                            data-testid="checkout-pay"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white px-8"
                        >
                            {redirecting ? (
                                <>
                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                    Redirecting…
                                </>
                            ) : (
                                `Continue · $${deal.price.toFixed(2)}`
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
